/**
 * NAV-6.3 — persist top-level sidebar NavGroup open/closed in one cookie.
 *
 * Cookie is the last *explicit* toggle. Missing cookie or missing key → open.
 * Navigating to a child of a closed group paints it open without writing here.
 *
 * ⚠️ NO COOKIE LIBRARY. `document.cookie` only. The parse/serialize path is
 * pure so the suite can run in vitest's node environment.
 */

export const SIDEBAR_NAV_GROUPS_COOKIE = 'joules_sidebar_nav_groups';

/** Abandoned after the one-shot migrate into {@link SIDEBAR_NAV_GROUPS_COOKIE}. */
export const LEGACY_COMMISSION_GROUP_OPEN_KEY = 'joules_sidebar_commission_group_open';

export const SIDEBAR_NAV_GROUP_IDS = [
  'workspace',
  'pipelines',
  'commission',
  'records',
  'admin',
] as const;

export type SidebarNavGroupId = (typeof SIDEBAR_NAV_GROUP_IDS)[number];
export type SidebarNavGroupMap = Partial<Record<SidebarNavGroupId, boolean>>;
export type CompleteSidebarNavGroupMap = Record<SidebarNavGroupId, boolean>;

const COOKIE_MAX_AGE_SECONDS = 31536000;

/**
 * Prefixes that count as "inside" a group. Matching is exact or `prefix/…`,
 * never a bare startsWith — `/app/pipeline` must not swallow `/app/pipeline-resi`.
 */
export const SIDEBAR_NAV_GROUP_PATHS: Record<SidebarNavGroupId, readonly string[]> = {
  workspace: ['/app/dashboards', '/app/dashboard', '/app/aurora', '/app/chat', '/app/earnings'],
  pipelines: ['/app/pipeline', '/app/pipeline-resi', '/app/onboarding'],
  commission: ['/app/commission'],
  records: ['/app/reporting', '/app/contracts', '/app/tracking'],
  admin: ['/app/sub-accounts', '/app/settings'],
};

export type SidebarNavGroupCookieIO = {
  cookieHeader?: string;
  protocol?: string;
  getLegacyLocalStorage?: () => string | null;
  removeLegacyLocalStorage?: () => void;
  setCookie?: (cookie: string) => void;
};

export function isSidebarNavGroupId(value: string): value is SidebarNavGroupId {
  return (SIDEBAR_NAV_GROUP_IDS as readonly string[]).includes(value);
}

/** Missing key or missing cookie → that group is open. */
export function isSidebarNavGroupOpen(
  map: SidebarNavGroupMap,
  id: SidebarNavGroupId,
): boolean {
  return map[id] !== false;
}

export function completeSidebarNavGroupMap(
  map: SidebarNavGroupMap,
): CompleteSidebarNavGroupMap {
  const out = {} as CompleteSidebarNavGroupMap;
  for (const id of SIDEBAR_NAV_GROUP_IDS) {
    out[id] = isSidebarNavGroupOpen(map, id);
  }
  return out;
}

export function parseSidebarNavGroupsCookie(
  raw: string | null | undefined,
): SidebarNavGroupMap {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const record = parsed as Record<string, unknown>;
    const out: SidebarNavGroupMap = {};
    for (const id of SIDEBAR_NAV_GROUP_IDS) {
      if (typeof record[id] === 'boolean') out[id] = record[id];
    }
    return out;
  } catch {
    return {};
  }
}

export function serializeSidebarNavGroupsCookie(map: SidebarNavGroupMap): string {
  return JSON.stringify(completeSidebarNavGroupMap(map));
}

export function readNamedCookie(cookieHeader: string, name: string): string | null {
  if (!cookieHeader) return null;
  for (const part of cookieHeader.split(';')) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');
    if (eq === -1) continue;
    if (trimmed.slice(0, eq) !== name) continue;
    const raw = trimmed.slice(eq + 1);
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return null;
}

export function buildSidebarNavGroupsSetCookie(
  map: SidebarNavGroupMap,
  opts: { secure: boolean; maxAge?: number } = { secure: false },
): string {
  const value = encodeURIComponent(serializeSidebarNavGroupsCookie(map));
  const maxAge = opts.maxAge ?? COOKIE_MAX_AGE_SECONDS;
  let cookie = `${SIDEBAR_NAV_GROUPS_COOKIE}=${value}; Path=/; SameSite=Lax; Max-Age=${maxAge}`;
  if (opts.secure) cookie += '; Secure';
  return cookie;
}

export function seedFromLegacyCommissionLocalStorage(
  cookiePresent: boolean,
  localStorageValue: string | null,
): SidebarNavGroupMap {
  if (cookiePresent) return {};
  if (localStorageValue === 'false') return { commission: false };
  return {};
}

export function pathMatchesGroupPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function isPathInSidebarNavGroup(
  id: SidebarNavGroupId,
  pathname: string,
): boolean {
  return SIDEBAR_NAV_GROUP_PATHS[id].some((prefix) =>
    pathMatchesGroupPrefix(pathname, prefix),
  );
}

/**
 * Cookie is the last explicit toggle. A child of a closed group paints open
 * for this visit without writing the cookie.
 */
export function paintedSidebarNavGroupOpen(
  map: SidebarNavGroupMap,
  id: SidebarNavGroupId,
  pathname: string,
): boolean {
  return isSidebarNavGroupOpen(map, id) || isPathInSidebarNavGroup(id, pathname);
}

function defaultCookieHeader(): string {
  return typeof document === 'undefined' ? '' : document.cookie;
}

function defaultProtocol(): string {
  return typeof location === 'undefined' ? 'http:' : location.protocol;
}

function defaultGetLegacy(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(LEGACY_COMMISSION_GROUP_OPEN_KEY);
  } catch {
    return null;
  }
}

function defaultRemoveLegacy(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LEGACY_COMMISSION_GROUP_OPEN_KEY);
  } catch {
    // Preference just will not be cleaned up.
  }
}

function defaultSetCookie(value: string): void {
  if (typeof document !== 'undefined') document.cookie = value;
}

export function writeSidebarNavGroups(
  map: SidebarNavGroupMap,
  io: SidebarNavGroupCookieIO = {},
): void {
  const protocol = io.protocol ?? defaultProtocol();
  const cookie = buildSidebarNavGroupsSetCookie(map, {
    secure: protocol === 'https:',
  });
  (io.setCookie ?? defaultSetCookie)(cookie);
}

/**
 * Read the cookie. If it is absent and the old Commission localStorage key is
 * the string `"false"`, seed `commission: false` into the first cookie write
 * and drop the localStorage key. After that, localStorage is never read again.
 */
export function loadSidebarNavGroups(
  io: SidebarNavGroupCookieIO = {},
): SidebarNavGroupMap {
  const header = io.cookieHeader ?? defaultCookieHeader();
  const raw = readNamedCookie(header, SIDEBAR_NAV_GROUPS_COOKIE);
  const cookiePresent = raw !== null;
  const map = parseSidebarNavGroupsCookie(raw);
  const getLegacy = io.getLegacyLocalStorage ?? defaultGetLegacy;
  const removeLegacy = io.removeLegacyLocalStorage ?? defaultRemoveLegacy;

  if (cookiePresent) {
    removeLegacy();
    return map;
  }

  const legacy = getLegacy();
  const seed = seedFromLegacyCommissionLocalStorage(false, legacy);
  if (legacy !== null) removeLegacy();

  if (Object.keys(seed).length === 0) return map;

  const merged = { ...map, ...seed };
  writeSidebarNavGroups(merged, io);
  return merged;
}
